import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Send, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const industries = [
  { value: 'recruitment', label: 'Recruitment / Staffing' },
  { value: 'logistics', label: 'Logistics / Supply Chain' },
  { value: 'manufacturing', label: 'Manufacturing / Operations' },
  { value: 'professional-services', label: 'Professional Services' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'other', label: 'Other' },
];

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    industry: '',
    message: '',
    honeypot: '', // Anti-spam honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    } else if (formData.company.trim().length < 2) {
      newErrors.company = 'Company must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.industry) {
      newErrors.industry = 'Please select an industry';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          company: formData.company.trim(),
          email: formData.email.trim().toLowerCase(),
          industry: formData.industry,
          message: formData.message.trim(),
          honeypot: formData.honeypot,
        }),
      });
      
      // Parse response body once
      let data;
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      
      // Handle error responses
      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Too many requests. Please wait a few minutes before trying again.');
        }
        if (response.status === 503) {
          throw new Error('Service temporarily unavailable. Please email us directly at hello@pyrtex.com');
        }
        throw new Error(data.detail || 'Failed to send message. Please try again.');
      }
      
      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch within 24 hours.');
      
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitError(error.message || 'Something went wrong. Please try again or email us directly.');
      toast.error(error.message || 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (submitError) {
      setSubmitError('');
    }
  };

  if (isSubmitted) {
    return (
      <section
        id="contact"
        data-testid="contact-section"
        className="bg-white py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="max-w-xl mx-auto text-center"
          >
            <div className="p-4 bg-slate-100 rounded-sm w-fit mx-auto mb-6">
              <CheckCircle className="text-green-600" size={48} strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 font-['Manrope'] mb-4">
              Message Received
            </h3>
            <p className="text-base text-slate-600">
              Thank you for reaching out. A member of our team will get back to you within 24 hours.
            </p>
            <Button
              onClick={() => {
                setIsSubmitted(false);
                setFormData({ name: '', company: '', email: '', industry: '', message: '', honeypot: '' });
              }}
              variant="outline"
              className="mt-8 uppercase text-xs tracking-wider"
              data-testid="submit-another"
            >
              Submit Another Request
            </Button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-white py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Left Column - Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-4">
              Get in Touch
            </p>
            <h2
              data-testid="contact-heading"
              className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 font-['Manrope'] mb-6 leading-tight"
            >
              Discuss your workflow
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mb-8">
              Tell us about your operational challenges. We'll schedule a consultation to explore how structured automation can help.
            </p>
            <div className="space-y-3 text-sm text-slate-500">
              <p>Response time: Within 24 hours</p>
              <p>Email: hello@pyrtex.com</p>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              {/* Honeypot field - hidden from users, catches bots */}
              <input
                type="text"
                name="website"
                value={formData.honeypot}
                onChange={(e) => handleChange('honeypot', e.target.value)}
                className="absolute opacity-0 pointer-events-none h-0 w-0"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                    Name
                  </Label>
                  <Input
                    id="name"
                    data-testid="input-name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    maxLength={100}
                    className={`rounded-sm border-slate-300 focus:border-slate-500 focus:ring-slate-500 ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                </div>

                {/* Company */}
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-sm font-medium text-slate-700">
                    Company
                  </Label>
                  <Input
                    id="company"
                    data-testid="input-company"
                    type="text"
                    placeholder="Your company"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    maxLength={100}
                    className={`rounded-sm border-slate-300 focus:border-slate-500 focus:ring-slate-500 ${errors.company ? 'border-red-500' : ''}`}
                  />
                  {errors.company && <p className="text-xs text-red-500">{errors.company}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Email
                  </Label>
                  <Input
                    id="email"
                    data-testid="input-email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    maxLength={254}
                    className={`rounded-sm border-slate-300 focus:border-slate-500 focus:ring-slate-500 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                </div>

                {/* Industry */}
                <div className="space-y-2">
                  <Label htmlFor="industry" className="text-sm font-medium text-slate-700">
                    Industry
                  </Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => handleChange('industry', value)}
                  >
                    <SelectTrigger
                      data-testid="select-industry"
                      className={`rounded-sm border-slate-300 focus:border-slate-500 focus:ring-slate-500 ${errors.industry ? 'border-red-500' : ''}`}
                    >
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((industry) => (
                        <SelectItem key={industry.value} value={industry.value}>
                          {industry.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.industry && <p className="text-xs text-red-500">{errors.industry}</p>}
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-sm font-medium text-slate-700">
                  What would you like to automate?
                </Label>
                <Textarea
                  id="message"
                  data-testid="input-message"
                  placeholder="Describe your workflow challenges and automation goals..."
                  rows={5}
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  maxLength={2000}
                  className={`rounded-sm border-slate-300 focus:border-slate-500 focus:ring-slate-500 resize-none ${errors.message ? 'border-red-500' : ''}`}
                />
                {errors.message && <p className="text-xs text-red-500">{errors.message}</p>}
              </div>

              {/* Submit Error */}
              {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-sm">
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="submit-button"
                disabled={isSubmitting}
                className="w-full sm:w-auto bg-slate-900 text-white hover:bg-slate-800 uppercase text-xs tracking-wider font-semibold px-10 py-6 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Submit
                    <Send className="ml-2" size={16} strokeWidth={1.5} />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
